/**
 * طبقة الاتصال للوحة مدير الأكاديمية.
 *
 * نقطتان في السيستم:
 *   • `GET /director/overview`         — نبض الأقسام · الطوابير اليتيمة ·
 *                                        الرحلات الواقفة · المحتاج مراجعة
 *   • `GET /director/list?kind=…`      — القايمة ورا أي رقم في اللوحة
 *   • `GET /director/digest`           — ملخّص اليوم وحالة قناة البريد
 *
 * ⛔ **ممنوع رقم مخترع في الواجهة.** الحقل اللي بيرجع `null` بيتعرض «—»
 *    مش صفر — الصفر ادّعاء إن إحنا عدّينا ولقينا ولا حاجة.
 * ⛔ **وممنوع «نجاح» على رد فاضي:** أي رد مش كائن/مش قائمة بيرمي خطأ صريح
 *    بدل ما الشاشة تفضل فاضية بلا سبب.
 */
import { ApiError, api } from "@/api/client";

/* ══════════════ أنواع ══════════════ */

export type DeptPulse = {
  dept: string;
  dept_ar: string;
  open: number;
  unassigned: number;
  overdue: number;
  blocked: number;
  closed_today: number;
  /** 🔻 `null` = العيّنة أصغر من إن المتوسط يبقى له معنى ⇒ «—» */
  avg_close_hours: number | null;
  avg_close_sample: number;
  members: number;
  /** 🔴 قسم مفيهوش موظف نشط — الطابور بتاعه بيتبلع بصمت */
  has_no_staff: boolean;
};

export type OrphanQueue = {
  dept: string;
  dept_ar: string;
  count: number;
  waiting_minutes: number;
  sample_title_ar: string;
};

export type StalledJourney = {
  id: string;
  subject_label_ar: string;
  current_step_no: number | null;
  steps_total: number;
  step_title_ar: string;
  owner_dept: string | null;
  owner_dept_ar: string;
  is_blocked: boolean;
  stalled_label_ar: string;
};

/** حساب شغّال ومستحيل يشوف أي حاجة — إعداد مش داتا */
export type MisconfiguredAccount = {
  user_id: string;
  name_ar: string;
  role: string | null;
  department: string | null;
  /** الرسالة بتقول **الحل** مش العرض */
  problem_ar: string;
};

export type NeedsReview = {
  pending_accounts: number;
  management_tasks: number;
  open_decisions: number;
  misconfigured_accounts: MisconfiguredAccount[];
  total: number;
};

export type Totals = {
  open: number;
  unassigned: number;
  overdue: number;
  blocked: number;
  closed_today: number;
  orphan_queues: number;
  orphan_tasks: number;
  stalled_journeys: number;
  needs_review: number;
};

export type Overview = {
  departments: DeptPulse[];
  orphan_queues: OrphanQueue[];
  stalled_journeys: StalledJourney[];
  needs_review: NeedsReview;
  totals: Totals;
  thresholds: {
    journey_stall_hours: number;
    orphan_queue_minutes: number;
    close_time_window_days: number;
    min_sample_for_average: number;
  };
  generated_at: string | null;
};

/** ⛔ نفس القايمة البيضا اللي في الباك إند — أي `kind` بره القايمة بيترفض هناك */
export type DrillKind =
  | "open"
  | "unassigned"
  | "overdue"
  | "blocked"
  | "closed_today"
  | "orphan_tasks"
  | "stalled_journeys"
  | "pending_accounts"
  | "management_tasks"
  | "open_decisions";

export type DrillRow = Record<string, unknown>;

export type DrillPage = {
  kind: DrillKind;
  dept: string | null;
  rows: DrillRow[];
  total: number;
  page: number;
  perPage: number;
  totalPages: number;
};

export type Digest = {
  day: string;
  text_ar: string;
  closed_today: number;
  overdue: number;
  blocked: number;
  unassigned: number;
  email_channel: { ready: boolean; waiting: number; note_ar: string };
};

/** عنوان عربي لكل قايمة — مصدر واحد عشان العنوان ما يكدبش على المحتوى */
export const DRILL_TITLES_AR: Record<DrillKind, string> = {
  open: "المهام المفتوحة",
  unassigned: "مستنية استلام",
  overdue: "المتأخرة",
  blocked: "واقفة مستنية حد تاني",
  closed_today: "اتقفل النهارده",
  orphan_tasks: "طلبات في طوابير مفيهاش حد",
  stalled_journeys: "الرحلات الواقفة",
  pending_accounts: "حسابات مستنية اعتماد",
  management_tasks: "مهام مفتوحة عند الإدارة",
  open_decisions: "قرارات ما اتفتحتش",
};

/* ══════════════ أدوات ══════════════ */

function fail(path: string, reason: string): never {
  // ⛔ ممنوع الفشل الصامت — الرد الفاضي عطل مش حالة عادية
  console.error(`[director] ${reason} (${path})`);
  throw new ApiError(
    502,
    { code: "EMPTY_RESPONSE", message_ar: "السيستم رجّع رد مش مفهوم — مقدرناش نعرض اللوحة" },
    null,
  );
}

function obj(v: unknown, path: string): Record<string, unknown> {
  if (!v || typeof v !== "object" || Array.isArray(v)) fail(path, "الرد مش كائن");
  return v as Record<string, unknown>;
}

function arr(v: unknown, path: string): Record<string, unknown>[] {
  if (!Array.isArray(v)) fail(path, "الرد مش قائمة");
  return v as Record<string, unknown>[];
}

function num(v: unknown, fallback = 0): number {
  return typeof v === "number" && Number.isFinite(v) ? v : fallback;
}

/** ⛔ الفرق بين «صفر» و«مش معروف» — الرقم الغائب بيفضل `null` مش صفر */
function numOrNull(v: unknown): number | null {
  return typeof v === "number" && Number.isFinite(v) ? v : null;
}

function str(v: unknown): string {
  return typeof v === "string" ? v : "";
}

function strOrNull(v: unknown): string | null {
  return typeof v === "string" && v.length > 0 ? v : null;
}

/* ══════════════ القراءة ══════════════ */

export async function fetchOverview(signal?: AbortSignal): Promise<Overview> {
  const path = "/director/overview";
  const res = await api.get<unknown>(path, { ...(signal ? { signal } : {}) });
  const d = obj(res.data, path);
  const totals = obj(d["totals"] ?? {}, path);
  const review = obj(d["needs_review"] ?? {}, path);
  const th = obj(d["thresholds"] ?? {}, path);

  return {
    departments: arr(d["departments"], path).map((r) => ({
      dept: str(r["dept"]),
      dept_ar: str(r["dept_ar"]),
      open: num(r["open"]),
      unassigned: num(r["unassigned"]),
      overdue: num(r["overdue"]),
      blocked: num(r["blocked"]),
      closed_today: num(r["closed_today"]),
      avg_close_hours: numOrNull(r["avg_close_hours"]),
      avg_close_sample: num(r["avg_close_sample"]),
      members: num(r["members"]),
      has_no_staff: r["has_no_staff"] === true,
    })),
    orphan_queues: arr(d["orphan_queues"], path).map((r) => ({
      dept: str(r["dept"]),
      dept_ar: str(r["dept_ar"]),
      count: num(r["count"]),
      waiting_minutes: num(r["waiting_minutes"]),
      sample_title_ar: str(r["sample_title_ar"]),
    })),
    stalled_journeys: arr(d["stalled_journeys"], path).map((r) => ({
      id: str(r["id"]),
      subject_label_ar: str(r["subject_label_ar"]),
      current_step_no: numOrNull(r["current_step_no"]),
      steps_total: num(r["steps_total"]),
      step_title_ar: str(r["step_title_ar"]),
      owner_dept: strOrNull(r["owner_dept"]),
      owner_dept_ar: str(r["owner_dept_ar"]),
      is_blocked: r["is_blocked"] === true,
      stalled_label_ar: str(r["stalled_label_ar"]),
    })),
    needs_review: {
      pending_accounts: num(review["pending_accounts"]),
      management_tasks: num(review["management_tasks"]),
      open_decisions: num(review["open_decisions"]),
      misconfigured_accounts: arr(review["misconfigured_accounts"] ?? [], path).map((a) => ({
        user_id: str(a["user_id"]),
        name_ar: str(a["name_ar"]),
        role: strOrNull(a["role"]),
        department: strOrNull(a["department"]),
        problem_ar: str(a["problem_ar"]),
      })),
      total: num(review["total"]),
    },
    totals: {
      open: num(totals["open"]),
      unassigned: num(totals["unassigned"]),
      overdue: num(totals["overdue"]),
      blocked: num(totals["blocked"]),
      closed_today: num(totals["closed_today"]),
      orphan_queues: num(totals["orphan_queues"]),
      orphan_tasks: num(totals["orphan_tasks"]),
      stalled_journeys: num(totals["stalled_journeys"]),
      needs_review: num(totals["needs_review"]),
    },
    thresholds: {
      journey_stall_hours: num(th["journey_stall_hours"], 48),
      orphan_queue_minutes: num(th["orphan_queue_minutes"], 30),
      close_time_window_days: num(th["close_time_window_days"], 30),
      min_sample_for_average: num(th["min_sample_for_average"], 3),
    },
    generated_at: strOrNull(d["generated_at"]),
  };
}

export async function fetchDrill(
  kind: DrillKind,
  opts: { dept?: string | null; page?: number; perPage?: number } = {},
): Promise<DrillPage> {
  const path = "/director/list";
  const page = opts.page ?? 1;
  const perPage = opts.perPage ?? 10;
  const res = await api.get<unknown>(path, {
    query: { kind, dept: opts.dept ?? null, page, per_page: perPage },
  });
  const rows = arr((res as { data?: unknown }).data, path);
  const meta = obj((res as { meta?: unknown }).meta ?? {}, path);
  const total = num(meta["total"]);
  return {
    kind,
    dept: opts.dept ?? null,
    rows,
    total,
    page: num(meta["page"], page),
    perPage: num(meta["per_page"], perPage),
    totalPages: Math.max(num(meta["total_pages"], 1), 1),
  };
}

export async function fetchDigest(): Promise<Digest> {
  const path = "/director/digest";
  const res = await api.get<unknown>(path);
  const d = obj(res.data, path);
  const ch = obj(d["email_channel"] ?? {}, path);
  return {
    day: str(d["day"]),
    text_ar: str(d["text_ar"]),
    closed_today: num(d["closed_today"]),
    overdue: num(d["overdue"]),
    blocked: num(d["blocked"]),
    unassigned: num(d["unassigned"]),
    email_channel: {
      ready: ch["ready"] === true,
      waiting: num(ch["waiting"]),
      note_ar: str(ch["note_ar"]),
    },
  };
}
